
import EditableBlock from '@/components/EditableBlock.vue'
import EditableBlockInput from '@/components/EditableBlockInput.vue'
import EditableBlockTextfield from '@/components/EditableBlockTextfield.vue'
import { computed, defineComponent, nextTick, onMounted, PropType, reactive, ref, toRefs, watch } from 'vue'
import {
  CtCategory,
  CtInventoryLocation,
  CtPacking,
  CtProduct,
  CtProp,
  CtStockItem
} from '@/models/interfaces/content-type'
import Directr, { ContentType } from '@/services/directr'
import HarmonicSystemsCode from '@/utils/hs'
import { useStore } from 'vuex'
import EditableBlockDimensionInput from '@/components/EditableBlockDimensionInput.vue'
import HSDict from '@/utils/constants/hs-codes'
import DButton from '@/components/base/DButton.vue'
import EyeIcon from '@/components/Icons/EyeIcon.vue'
import WarehouseInformation from '@/pages/Products/ProductDetail/components/WarehouseInformation.vue'
import Dismissable from '@/components/Dismissable.vue'
import SearchableMultiSelect from '@/components/SearchableMultiSelect.vue'
import LabelBox from '@/pages/Products/components/LabelBox.vue'
import AddIcon from '@/components/Icons/AddIcon.vue'
import TickIcon from '@/components/Icons/TickIcon.vue'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import PenIcon from '@/components/Icons/PenIcon.vue'
import { useQuasar } from 'quasar'
import PackingGroupModal from '@/pages/Products/ProductDetail/components/PackingGroupModal.vue'
import PropertyInformation from '@/pages/Products/ProductDetail/components/PropertyInformation.vue'
import MinusCircleIcon from '@/components/Icons/MinusCircleIcon.vue'
import PlusCircleIcon from '@/components/Icons/PlusCircleIcon.vue'
import { generateUUID } from '@Protium/vue-ozone-utils'
import _ from 'lodash'
import Tooltip from '@/components/Tooltip.vue'
import tooltips from '@/constants/tooltips'
import InfoButton from '@/components/Icons/InfoButton.vue'
import PackingTable from '@/pages/Products/ProductDetail/components/PackingTable.vue'
import { validateEan } from '@/utils/input'
import { isUUID } from '@/utils'

export default defineComponent({
  name: 'StockInformationEditState',
  components: {
    HarmonicSystemsCode,
    PackingTable,
    InfoButton,
    Tooltip,
    PlusCircleIcon,
    MinusCircleIcon,
    PropertyInformation,
    PackingGroupModal,
    PenIcon,
    TrashIcon,
    CloseIcon,
    TickIcon,
    AddIcon,
    LabelBox,
    SearchableMultiSelect,
    Dismissable,
    WarehouseInformation,
    EyeIcon,
    DButton,
    EditableBlockInput,
    EditableBlock,
    EditableBlockDimensionInput,
    EditableBlockTextfield
  },

  emits: ['update:component', 'update:editing', 'cancel'],

  props: {
    component: {
      type: Object,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    productId: {
      type: String,
      required: true,
    },
    propValues: {
      type: Object as PropType<{ [key: string]: Array<string> }>,
      default: () => ({})
    }
  },

  setup (props, { emit }) {
    const $store = useStore()
    const $q = useQuasar()
    const $directr: Directr = $store.getters['Services/Directr']
    const $resolver = $store.getters['Services/Directr']
    const units = ['mm', 'cm', 'dm', 'm']
    const weightUnits = ['mg', 'g', 'kg']
    const HSOptions = ref(HSDict.asOptions.map(o => o.label))

    const emitUpdate = async (product, productId, $event) => {
      // console.log('emitUpdate', $event)
      if (newProps.value) {
        for (const [propId, prop] of Object.entries(newProps.value)) {
          const newProp = await $directr.put(ContentType.Prop, prop).then(({ items = [] }) => items[0])
          allProps.value.push(newProp)
          // set the correct sys.id references for the newly created props
          $event.props = Object.fromEntries(
            Object.entries($event.props).map(([oldPropId, value]) => {
              if (oldPropId === propId) {
                return [newProp.sys.id, value]
              }
              return [oldPropId, value]
            })
          )
        }
      }
      // prevents saving empty meta fields
      if ($event.meta && '' in $event.meta) delete $event.meta['']
      if ('categories' in $event)
        $event.categories = $event.categories.map(c => c.sys?.id || c)

      $event.packing = selectedFormattedPacking.value.map(sfp => sfp.value)
      emit('update:component', productId, $event)
    }

    const emitUpdateEditing = (value) => {
      emit('update:editing', value)
    }

    const state = reactive({
      expanded: false,
      showCreatePackingGroup: false,
      // showCreateStockItem: false,
      modalPackingCategoryId: null,
      // stockInformation: null as CtInventoryLocation | null,
      // productInformation: null as CtProduct | null,
      // packingAndCategories: [],
      packing: [] as Array<CtPacking>,
      categories: [] as Array<CtCategory>,
      packingCategories: [] as Array<CtCategory>,
      // showMetaFieldsList: false
    })

    const allProps = ref<CtProp[]>([])
    const newProps = ref<{ [key: string]: CtProp }>({})

    const getAllProps = async () => {
      allProps.value = await $resolver.get(ContentType.Prop).then(({ items = [] }) => items)
    }
    void getAllProps()

    // const getProductLinks = async () => {
    //   if (!props.productId) return
    //   const { items: productInformation } = await $directr.get('product.links', { 'id': props.productId })

    //   if (productInformation) {
    //     state.productInformation = productInformation[0]
    //   }
    // }

    const addMetaField = (editState): void => {
      if (!editState.meta) editState.meta = {}
      editState.meta[''] = ''
    }

    const removeMetaField = (index, editState): void => {
      const meta = Object.entries(editState.meta)
      if (meta && Array.isArray(meta[index]) && meta[index].length) {
        meta.splice(index, 1)
        editState.meta = Object.fromEntries(meta)
      }
    }

    const updateKey = (index: number, newKey: string, editState): void => {
      const meta = Object.entries(editState.meta)
      if (meta && Array.isArray(meta[index]) && meta[index].length > 0) {
        meta[index][0] = newKey
        editState.meta = Object.fromEntries(meta)
      }
    }

    const updateValue = (key: string, value: string, editState): void => {
      editState.meta[key] = value
    }

    const getPackingValues = async () => {
      await $directr.get(ContentType.Packing).then(response => {
        state.packing = (response.items || [])
      })

      await $directr.get(ContentType.Category, [
        { field: 'type', method: 'is', match: 'product' },
        { field: 'type', method: 'is', match: 'packing' }
      ], { query: { any: true } }).then(response => {
        state.packingCategories = (response.items || []).filter(c => c.type === 'packing')
        state.categories = (response.items || []).filter(c => c.type === 'product')
      })

      selectFormattedPacking()
    }

    const formattedPacking = computed(() => [
      ...state.packing.map(p => ({ ...p, type: 'Packing' })),
      ...state.packingCategories.map(c => ({ ...c, type: 'Packing Group' }))
    ].map((obj) => ({
      value: obj.sys.id,
      label: obj.name?.toString(),
      type: obj.type
    }))
    )

    const selectedFormattedPacking = ref()
    const selectFormattedPacking = () => {
      const packingArrays = [...(props.component?.packing ?? [])]
      selectedFormattedPacking.value = formattedPacking.value.filter(fp => (packingArrays ?? []).map(p => p.sys.id).includes(fp.value))
    }
    watch(() => [props?.component?.packing], selectFormattedPacking)

    // code below appears to be redundant
    // watch(() => formattedPacking.value, (newValue, oldValue) => {
    //   if (!oldValue.length) {
    //     const packingArrays = [...(props.component?.packing ?? []), ...(props.component?.categories ?? [])]
    //     selectedFormattedPacking.value = formattedPacking.value.filter(fp => (packingArrays ?? []).map(p => p.sys.id).includes(fp.value))
    //   }
    // })
    // watch(() => [props?.component?.packing], () => {
    //   const packingArrays = [...(props.component?.packing ?? []), ...(props.component?.categories ?? [])]
    //   selectedFormattedPacking.value = formattedPacking.value.filter(fp => (packingArrays ?? []).map(p => p.sys.id).includes(fp.value))
    // })
    // watch(() => [props?.component?.categories], () => {
    //   const packingArrays = [...(props.component?.packing ?? []), ...(props.component?.categories ?? [])]
    //   selectedFormattedPacking.value = formattedPacking.value.filter(fp => (packingArrays ?? []).map(p => p.sys.id).includes(fp.value))
    // })

    const filteredFormattedPacking = ref()
    const filterFn = (val, update) => {
      update(() => {
        if (!val?.length) {
          filteredFormattedPacking.value = formattedPacking.value
        } else {
          const needle = val.toLowerCase()
          filteredFormattedPacking.value = formattedPacking.value.filter(fp => (fp.label ?? '').toLowerCase().indexOf(needle) > -1)
        }
      })
    }

    const filterHSFn = (val, update) => {
      update(() => {
        const needle = val.toLowerCase()
        HSOptions.value = HSDict.asOptions.filter((v) => { return v.label.toLowerCase().indexOf(needle) > -1 || v.value.indexOf(needle) > -1 }) as any[]
        return HSOptions.value
      })
    }

    const onPackingGroupClick = (opt) => {
      state.modalPackingCategoryId = opt.value
      state.showCreatePackingGroup = true
    }

    const onPackingGroupSave = (savedCat, editState) => {
      const matchingCatIndex = state.packingCategories.findIndex(c => c.sys.id === savedCat.sys.id)
      if (matchingCatIndex === -1) {
        state.packingCategories.push(savedCat)
      } else {
        state.packingCategories[matchingCatIndex] = savedCat
      }
      const matchingSelectedIndex = selectedFormattedPacking.value.findIndex(fp => fp.value === savedCat.sys.id)
      if (matchingSelectedIndex === -1) {
        selectedFormattedPacking.value.push({
          label: savedCat.name?.toString(),
          value: savedCat.sys.id,
          type: 'Packing Group'
        })
      } else {
        selectedFormattedPacking.value[matchingSelectedIndex].label = savedCat.name?.toString()
      }
      state.showCreatePackingGroup = false
      state.modalPackingCategoryId = null
    }

    const getEan = (eanArr) => {
      return eanArr
    }

    const setEan = (event, editState) => {
      if (!editState.ean) editState.ean = []
      event.map(e => {
        if (editState.ean.includes(e.label)) {
          $q.notify({
            type: 'negative',
            position: 'bottom',
            message: 'The product already includes this ean',
            classes: 'custom-notify error'
          })
          return
        }
        if (e.label) editState.ean.push(e.label)
      })
      return true
    }

    const getHS = (hsCode) => {
      if (hsCode?.value)
        return (`${hsCode.value}  - ${hsCode.label}`)
      else if (typeof hsCode === 'number' || typeof hsCode === 'string') {
        const trimmedHScode = String(hsCode || '').substring(0,6)
        const spec = HSDict.byCode[String(trimmedHScode)]

        if (!spec) return `invalid HS code "${trimmedHScode}"`
        return `${spec.hscode} — ${spec.description}`
      }

      return ''
    }

    const setHS = (editState, event) => {
      editState.hs = event
    }

    onMounted(() => {
      // void getProductLinks()
      void getPackingValues()
    })

    const categoryHandlers = {
      create: async ({ name, label }) => {
        const created = await $directr.put(ContentType.Category, { name, label, type: 'product' }).then(({ items }) => items?.[0])
        return created
      }
    }

    function updateCategories (items: Array<CtCategory>) {

      items.forEach(cat => {
        const index = state.categories.findIndex(c => c.sys?.id === cat.sys?.id)
        if (index < 0) state.categories.push(cat)
      })
    }

    return {
      model: ref(null),
      HSOptions,
      ...toRefs(state),
      addCategory: (editState, e) => {
        editState.categories = (editState.categories || []).concat(e).filter((e, i, a) => Boolean(e) && isUUID(e) && a.indexOf(e) === i)
        // console.log(editState.categories)
      },
      categoryHandlers,
      //validateHstCode,
      HSDict,
      updateCategories,
      units,
      weightUnits,
      emitUpdate,
      emitUpdateEditing,
      addMetaField,
      removeMetaField,
      updateValue,
      updateKey,
      getPackingValues,
      selectedFormattedPacking,
      filteredFormattedPacking,
      filterFn,
      filterHSFn,
      allProps,
      newProps,
      onPackingGroupClick,
      onPackingGroupSave,
      getEan,
      setEan,
      getHS,
      setHS,
      tooltips,
      validateEan
    }
  }
})
